import { useRef, useState } from "react";
import { api, API } from "@/lib/api";
import { toast } from "sonner";
import { Upload, X, Loader2 } from "lucide-react";

/**
 * Multi-image uploader. `value` is an array of absolute or relative image URLs.
 * Supports drag & drop + file picker; backend returns `/api/files/...` paths.
 */
export default function ImageUploader({ value = [], onChange, max = 10 }) {
  const [loading, setLoading] = useState(false);
  const [drag, setDrag] = useState(false);
  const inputRef = useRef(null);
  const backend = process.env.REACT_APP_BACKEND_URL;

  const toAbs = (u) => (u?.startsWith("http") ? u : `${backend}${u}`);

  const uploadFiles = async (files) => {
    const arr = Array.from(files).filter((f) => f.type.startsWith("image/"));
    if (arr.length === 0) return;
    setLoading(true);
    const uploaded = [];
    for (const f of arr) {
      const fd = new FormData();
      fd.append("file", f);
      try {
        const { data } = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        uploaded.push(data.url);
      } catch (e) {
        toast.error(`Falha ao enviar ${f.name}`);
      }
    }
    if (uploaded.length) {
      onChange([...(value || []), ...uploaded].slice(0, max));
      toast.success(`${uploaded.length} imagem(ns) enviada(s)`);
    }
    setLoading(false);
  };

  const onDrop = (e) => {
    e.preventDefault(); setDrag(false);
    uploadFiles(e.dataTransfer.files);
  };

  const remove = (idx) => onChange((value || []).filter((_, i) => i !== idx));

  return (
    <div>
      <div
        onClick={() => inputRef.current?.click()}
        onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
        onDragLeave={() => setDrag(false)}
        onDrop={onDrop}
        className={`cursor-pointer border-2 border-dashed p-6 text-center transition-colors ${drag ? "border-[#49392F] bg-[#E8DCCB]/40" : "border-[#C2A064]/40 bg-white"}`}
        data-testid="image-uploader"
      >
        {loading ? (
          <div className="flex items-center justify-center gap-2 text-[#49392F]">
            <Loader2 size={16} className="animate-spin" /> Enviando...
          </div>
        ) : (
          <div className="text-[#49392F]/70 text-[13px]">
            <Upload size={18} className="mx-auto mb-2 text-[#C2A064]" />
            Arraste imagens aqui ou <span className="underline">clique para selecionar</span>
            <div className="text-[11px] text-[#49392F]/50 mt-1">JPG, PNG, WebP — otimizadas automaticamente</div>
          </div>
        )}
        <input ref={inputRef} type="file" accept="image/*" multiple hidden onChange={(e) => uploadFiles(e.target.files)} />
      </div>

      {value?.length > 0 && (
        <div className="mt-3 grid grid-cols-3 md:grid-cols-5 gap-2">
          {value.map((u, i) => (
            <div key={i} className="relative aspect-square bg-[#F8F5EF] overflow-hidden group">
              <img src={toAbs(u)} alt={`img-${i}`} className="w-full h-full object-cover" />
              <button type="button" onClick={() => remove(i)} className="absolute top-1 right-1 bg-[#49392F] text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity">
                <X size={12} />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
